import { supabase } from "@/integrations/supabase/client";

export type AdminLevel = 'super_admin' | 'template_admin' | 'support_admin' | 'content_admin';

export interface AdminUser {
  id: string;
  user_id: string;
  admin_level: AdminLevel;
  permissions: any;
  is_active: boolean;
  created_at: string;
}

export async function isAdmin(userId?: string): Promise<boolean> {
  if (!userId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    userId = user.id;
  }

  const { data, error } = await supabase.rpc('is_admin', { _user_id: userId });
  if (error) {
    console.error('is_admin RPC error', error);
    return false;
  }

  return Boolean(data);

}

export async function getAdminUser(userId?: string): Promise<AdminUser | null> {
  if (!userId) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;
    userId = user.id;
  }

  const { data } = await supabase
    .from('admin_users')
    .select('*')
    .eq('user_id', userId)
    .eq('is_active', true)
    .single();

  return data;
}

export async function hasPermission(permission: string, userId?: string): Promise<boolean> {
  const adminUser = await getAdminUser(userId);
  
  if (!adminUser) return false;
  if (adminUser.admin_level === 'super_admin') return true;
  
  return adminUser.permissions?.[permission] === true;
}

export async function logAdminAction(
  action: string,
  resourceType: string,
  resourceId?: string,
  details?: Record<string, any>
) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  await supabase
    .from('admin_audit_log')
    .insert({
      admin_user_id: user.id,
      action_type: action,
      resource_type: resourceType,
      resource_id: resourceId,
      action_details: details || {},
    });
}

export function isSuperAdmin(adminUser: AdminUser | null): boolean {
  return adminUser?.admin_level === 'super_admin';
}

export function isTemplateAdmin(adminUser: AdminUser | null): boolean {
  return adminUser?.admin_level === 'template_admin' || isSuperAdmin(adminUser);
}
